/**
 * PaidOutsTab Component
 * Handles lottery/scratch card prizes and general paid outs
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Ticket, Gift, Banknote, Plus, Trash2, Pencil, AlertTriangle } from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

const formatAmount = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? '0.00' : num.toFixed(2);
};

export const PaidOutsTab = ({
  // Feature flags
  hasLottery,
  hasScratchCards,
  // Prize paid state
  lotteryPrizePaid,
  setLotteryPrizePaid,
  scratchPrizePaid,
  setScratchPrizePaid,
  // Paid outs data
  paidOuts,
  paidOutTypes,
  suppliers,
  creditors,
  // New paid out form
  newPaidOut,
  setNewPaidOut,
  // Actions
  addPaidOut,
  removePaidOut,
  editPaidOut,
  editingPaidOutId,
  cancelEditPaidOut,
  // Computed
  totalPaidOut
}) => {
  
  const handleAmountFocus = (e) => {
    e.target.select();
  };

  const handlePrizeChange = (setter) => (e) => {
    if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
      setter(e.target.value);
    }
  };

  const totalPrizePaid = (parseFloat(lotteryPrizePaid) || 0) + (parseFloat(scratchPrizePaid) || 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Lottery & Scratch Card Prizes Paid Out */}
      {(hasLottery || hasScratchCards) && (
        <Card>
          <CardHeader className="bg-yellow-500/5 border-b border-yellow-500/20 pb-3">
            <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
              <Ticket className="h-4 w-4 text-yellow-500" />
              Prizes Paid Out
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 space-y-2">
            {hasLottery && (
              <div className="flex items-center gap-3">
                <Label className="flex-1 flex items-center gap-2 text-sm">
                  <Ticket className="h-3.5 w-3.5 text-yellow-500" />
                  Lottery Prize
                </Label>
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                  <Input
                    type="text"
                    value={lotteryPrizePaid}
                    onChange={handlePrizeChange(setLotteryPrizePaid)}
                    onBlur={() => setLotteryPrizePaid(formatAmount(lotteryPrizePaid))}
                    onFocus={handleAmountFocus}
                    className="pl-6 h-8 text-sm font-mono text-right"
                    placeholder="0.00"
                    data-testid="lottery-prize"
                  />
                </div>
              </div>
            )}
            {hasScratchCards && (
              <div className="flex items-center gap-3">
                <Label className="flex-1 flex items-center gap-2 text-sm">
                  <Gift className="h-3.5 w-3.5 text-purple-500" />
                  Scratch Card Prize
                </Label>
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                  <Input
                    type="text"
                    value={scratchPrizePaid}
                    onChange={handlePrizeChange(setScratchPrizePaid)}
                    onBlur={() => setScratchPrizePaid(formatAmount(scratchPrizePaid))}
                    onFocus={handleAmountFocus}
                    className="pl-6 h-8 text-sm font-mono text-right"
                    placeholder="0.00"
                    data-testid="scratch-prize"
                  />
                </div>
              </div>
            )}
            
            {/* Total for Prize Paid Outs */}
            <div className="border-t border-border pt-2 mt-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Total Prize Paid</span>
                <span className="font-bold font-mono text-yellow-600 dark:text-yellow-400">
                  {formatCurrency(totalPrizePaid)}
                </span>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground">
              Deducted from total when calculating variance
            </p>
          </CardContent>
        </Card>
      )}

      {/* Paid Outs Section */}
      <Card>
        <CardHeader className="bg-red-500/5 border-b border-red-500/20 pb-3">
          <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
            <Banknote className="h-4 w-4 text-red-500" />
            Paid Outs
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-3 space-y-2">
          {paidOuts.map(paidOut => (
            <div key={paidOut.id} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${paidOut.till_warning ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-muted/50'} ${editingPaidOutId === paidOut.id ? 'ring-2 ring-primary' : ''}`} data-testid={`paidout-row-${paidOut.id}`}>
              <div className="flex-1">
                <p className="font-medium flex items-center gap-1">
                  {paidOut.till_warning && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                  {paidOut.description}
                </p>
                <p className="text-xs text-muted-foreground">
                  {paidOut.paid_out_type_name}
                  {paidOut.supplier_name && ` - ${paidOut.supplier_name}`}
                  {paidOut.creditor_name && ` - ${paidOut.creditor_name}`}
                </p>
              </div>
              <span className="font-mono">{formatCurrency(paidOut.amount)}</span>
              <Button variant="ghost" size="icon" onClick={() => editPaidOut(paidOut)} data-testid={`paidout-edit-${paidOut.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => removePaidOut(paidOut.id)} data-testid={`paidout-delete-${paidOut.id}`}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}

          <div className="border border-border rounded-lg p-4 space-y-3">
            {/* 1. Paid Out Type */}
            <Select 
              value={newPaidOut.paid_out_type_id}
              onValueChange={(value) => {
                const type = paidOutTypes.find(t => t.id === value);
                setNewPaidOut({ 
                  ...newPaidOut, 
                  paid_out_type_id: value,
                  paid_out_type_name: type?.name || '',
                  supplier_id: '',
                  supplier_name: '',
                  creditor_id: '',
                  creditor_name: ''
                });
              }}
            >
              <SelectTrigger data-testid="paidout-type-select">
                <SelectValue placeholder="Select Paid Out Type *" />
              </SelectTrigger>
              <SelectContent>
                {paidOutTypes.map(type => (
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* 2. Supplier dropdown - shown only when type is "Supplier Payment" */}
            {newPaidOut.paid_out_type_name === 'Supplier Payment' && (
              <Select 
                value={newPaidOut.supplier_id}
                onValueChange={(value) => {
                  const supplier = suppliers.find(s => s.id === value);
                  setNewPaidOut({ 
                    ...newPaidOut, 
                    supplier_id: value,
                    supplier_name: supplier?.name || ''
                  });
                }}
              >
                <SelectTrigger data-testid="paidout-supplier-select">
                  <SelectValue placeholder="Select Supplier *" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.length === 0 ? (
                    <SelectItem value="no-suppliers" disabled>No suppliers configured for this store</SelectItem>
                  ) : (
                    [...suppliers].sort((a, b) => a.name.localeCompare(b.name)).map(supplier => (
                      <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}

            {/* 2. Creditor dropdown - shown only when type is "Creditors" */}
            {newPaidOut.paid_out_type_name === 'Creditors' && (
              <Select 
                value={newPaidOut.creditor_id}
                onValueChange={(value) => {
                  const creditor = creditors.find(c => c.id === value);
                  setNewPaidOut({ 
                    ...newPaidOut, 
                    creditor_id: value,
                    creditor_name: creditor?.name || ''
                  });
                }}
              >
                <SelectTrigger data-testid="paidout-creditor-select">
                  <SelectValue placeholder="Select Creditor *" />
                </SelectTrigger>
                <SelectContent>
                  {creditors.length === 0 ? (
                    <SelectItem value="no-creditors" disabled>No creditors configured for this store</SelectItem>
                  ) : (
                    [...creditors].sort((a, b) => a.name.localeCompare(b.name)).map(creditor => (
                      <SelectItem key={creditor.id} value={creditor.id}>{creditor.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}

            {/* 3. Description */}
            <Input
              placeholder="Description *"
              value={newPaidOut.description}
              onChange={(e) => setNewPaidOut({ ...newPaidOut, description: e.target.value })}
              data-testid="paidout-description"
            />

            {/* 4. Amount */}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
              <Input
                type="text"
                placeholder="0.00"
                value={newPaidOut.amount}
                onChange={(e) => {
                  if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                    setNewPaidOut({ ...newPaidOut, amount: e.target.value });
                  }
                }}
                onBlur={() => setNewPaidOut({ ...newPaidOut, amount: formatAmount(newPaidOut.amount) })}
                className="pl-7"
                data-testid="paidout-amount"
              />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={addPaidOut} data-testid="add-paidout-btn">
                {editingPaidOutId ? <Pencil className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                {editingPaidOutId ? 'Update Paid Out' : 'Add Paid Out'}
              </Button>
              {editingPaidOutId && (
                <Button type="button" variant="ghost" onClick={cancelEditPaidOut} data-testid="cancel-edit-paidout-btn">Cancel</Button>
              )}
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <span className="font-medium">Total Paid Out</span>
              <span className="font-bold font-mono">{formatCurrency(totalPaidOut)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaidOutsTab;
